import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { Control, Controller } from 'react-hook-form';

import { FormGroup } from 'components/chroma';
import { OptionGroup, Select } from 'components/chroma/extended';
import { ApiData } from 'scenes/common/types';
import {
  getApiData,
  getApiErrorMessage,
  hasData,
  isApiError,
  makeApiSuccess,
} from 'scenes/common/helpers/apiData';
import { VehicleManualQuoteDetailsField } from 'scenes/motor/types';
import analytics from 'scenes/motor/analytics';
import { MotorFunnelState } from 'scenes/motor/flows/types';

import { itemsToValues } from './service/format';

const VehicleManualSelect: FunctionComponent<{
  funnelState: MotorFunnelState;
  fieldName: VehicleManualQuoteDetailsField;
  getItems: () => Promise<ApiData<string[]>>;
  label: string;
  control: Control<Record<VehicleManualQuoteDetailsField, string>>;
  defaultValue: string;
  afterOnChange: (fieldName: VehicleManualQuoteDetailsField) => void;
}> = ({
  funnelState,
  fieldName,
  getItems,
  label,
  control,
  defaultValue = '',
  afterOnChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<ApiData<string[]>>(makeApiSuccess([]));

  useEffect(() => {
    if (isApiError(items)) {
      analytics().error(
        funnelState,
        `Vehicle api: ${getApiErrorMessage(items)}`
      );
    }
  }, [funnelState, items]);

  useEffect(() => {
    let componentMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      const data = await getItems();
      if (componentMounted) {
        setItems(data);
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      componentMounted = false;
    };
  }, [getItems]);

  return (
    <FormGroup
      label={label}
      id={fieldName}
      error={items && isApiError(items) ? 'Unable to load items' : ''}
    >
      <Controller
        name={fieldName}
        defaultValue={defaultValue}
        rules={{ required: true }}
        control={control}
        render={({ onChange, value }) => (
          <Select
            id={fieldName}
            fieldName={fieldName}
            value={value}
            onChange={(_: ChangeEvent, selected: string) => {
              onChange(selected);
              afterOnChange(fieldName);
            }}
            required
            valid={Boolean(value)}
            isLoading={isLoading}
          >
            <option value="" disabled>
              Select
            </option>
            <OptionGroup
              optionList={
                hasData(items) ? itemsToValues(getApiData(items)) : []
              }
            />
          </Select>
        )}
      />
    </FormGroup>
  );
};

export default VehicleManualSelect;
